/**
 * InfoMeterPage – Intelligentes Zähler-Modul (Typ: "strom" und "gas").
 * Features: HT/NT-Ablesung (Strom) / m³-Ablesung (Gas), Auto-Berechnung, Sparkline,
 *           Anomalie-Warnung, Foto-Upload (optional), CSV/PDF-Export, Standort-Isolierung.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import {
  Zap, Flame, Droplets, Plus, Trash2, FileDown, Camera, AlertTriangle, TrendingUp,
  TrendingDown, Minus, Image, Loader2, Settings, Pencil, RefreshCw,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmtKwh = (v) =>
  v == null ? '–' : `${Number(v).toLocaleString('de-DE', { maximumFractionDigits: 0 })} kWh`;

const fmtM3 = (v, decimals = 0) =>
  v == null ? '–' : `${Number(v).toLocaleString('de-DE', { maximumFractionDigits: decimals })} m³`;

const fmtDate = (iso) => {
  if (!iso) return '–';
  try { return new Date(iso).toLocaleDateString('de-DE'); }
  catch { return iso.slice(0, 10); }
};

// ─── Delta-Badge ──────────────────────────────────────────────────────────────
function DeltaBadge({ pct, isAnomaly }) {
  if (pct == null) return <span className="text-muted-foreground text-xs">–</span>;
  const positive = pct > 0;
  const Icon = pct > 5 ? TrendingUp : pct < -5 ? TrendingDown : Minus;
  const color = isAnomaly ? 'text-orange-500' : positive ? 'text-red-500' : 'text-green-600';
  return (
    <span className={`flex items-center gap-0.5 text-xs font-medium ${color}`}>
      <Icon className="h-3 w-3" />
      {pct > 0 ? '+' : ''}{pct.toFixed(1)} %
      {isAnomaly && <AlertTriangle className="h-3 w-3 ml-0.5" />}
    </span>
  );
}

// ─── Hauptkomponente ──────────────────────────────────────────────────────────
export default function InfoMeterPage() {
  const { moduleId } = useParams();
  const { user } = useAuth();
  const isAdmin = ['admin', 'superadmin', 'stationsleitung'].includes(user?.role);
  const canEdit = user?.role === 'admin';
  const canDelete = user?.role === 'admin';
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const [module, setModule] = useState(null);
  const [entries, setEntries] = useState([]);
  const [locations, setLocations] = useState([]);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);

  // Modul-Typ – wird nach dem Laden gesetzt
  const isGas = module?.meter_type === 'gas';
  const isWater = module?.meter_type === 'water';
  const ModuleIcon = isGas ? Flame : isWater ? Droplets : Zap;
  const moduleColor = isGas ? 'text-orange-500' : isWater ? 'text-blue-500' : 'text-yellow-500';
  const moduleBg = isGas ? 'bg-orange-50 dark:bg-orange-900/20' : isWater ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-yellow-50 dark:bg-yellow-900/20';

  // Dialog
  const [showDialog, setShowDialog] = useState(false);
  const [editEntryId, setEditEntryId] = useState(null);
  const [dialogLocId, setDialogLocId] = useState('');
  const [form, setForm] = useState({ ht_stand: '', nt_stand: '', m3_stand: '', reading_date: '' });
  const [isMeterReset, setIsMeterReset] = useState(false);
  const [newMeterNr, setNewMeterNr] = useState('');
  const [newMeterFaktor, setNewMeterFaktor] = useState('');
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [lastEntry, setLastEntry] = useState(null);
  const [saving, setSaving] = useState(false);
  const photoRef = useRef();

  // Wasser: Zähler-Auswahl
  const [selectedZaehlerId, setSelectedZaehlerId] = useState('');

  // Config Dialog
  const [showConfig, setShowConfig] = useState(false);
  const [cfgForm, setCfgForm] = useState({ meter_config: {}, location_meter_configs: [] });
  const [savingCfg, setSavingCfg] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const params = selectedLocation ? { location_id: selectedLocation } : {};
      const [mRes, eRes, lRes, sRes] = await Promise.all([
        axios.get(`${API}/api/info/modules`, { headers }),
        axios.get(`${API}/api/info/${moduleId}/entries`, { headers, params }),
        axios.get(`${API}/api/user/locations`, { headers }),
        axios.get(`${API}/api/info/${moduleId}/meter-stats`, { headers, params }),
      ]);
      const mod = (mRes.data || []).find(m => m.id === moduleId);
      setModule(mod || null);
      setEntries(eRes.data || []);
      const locs = lRes.data || [];
      setLocations(locs);
      if (!isAdmin && locs.length === 1 && !selectedLocation) setSelectedLocation(locs[0].id);

      // Statistiken für Sparkline aggregieren
      const statsData = sRes.data || [];
      if (selectedLocation) {
        const locStat = statsData.find(s => s.location_id === selectedLocation);
        setMonthlyStats(locStat?.monthly_data || []);
      } else {
        const agg = {};
        statsData.forEach(loc =>
          loc.monthly_data.forEach(({ monat, verbrauch_kwh }) => {
            agg[monat] = (agg[monat] || 0) + verbrauch_kwh;
          })
        );
        setMonthlyStats(Object.entries(agg).sort().map(([monat, verbrauch_kwh]) => ({ monat, verbrauch_kwh })));
      }
    } catch {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [moduleId, selectedLocation]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const locName = (id) => locations.find(l => l.id === id)?.name || id || '–';

  const getLocConfig = useCallback((locId) =>
    (module?.location_meter_configs || []).find(c => c.location_id === locId) || null,
    [module]);

  // NT-Detection: neues has_nt Feld ODER altes zaehler_nr_nt (Backward-Compat)
  const getHasNt = useCallback((locId) => {
    const cfg = getLocConfig(locId);
    if (!cfg) return false;
    return cfg.has_nt === true || !!cfg.zaehler_nr_nt;
  }, [getLocConfig]);

  // Zählernummer eines Standorts (neues zaehler_nr ODER altes zaehler_nr_ht)
  const getZaehlerNr = useCallback((locId) => {
    const cfg = getLocConfig(locId);
    if (!cfg) return null;
    return cfg.zaehler_nr || cfg.zaehler_nr_ht || null;
  }, [getLocConfig]);
  // Wasser: Alle Zähler für einen Standort
  const getWaterMeters = useCallback((locId) => {
    if (!locId) return [];
    return (module?.location_meter_configs || []).filter(c => c.location_id === locId);
  }, [module]);

  // Live-Vorschau der Berechnung während der Eingabe
  const previewKwh = useMemo(() => {
    if (isMeterReset) return null;
    if (isWater) {
      // Wasser: reiner m³-Diff, keine Umrechnung
      if (!form.m3_stand || !lastEntry?.row_data?.m3_stand) return null;
      const m3Diff = parseFloat(form.m3_stand) - parseFloat(lastEntry.row_data.m3_stand || 0);
      if (m3Diff < 0) return 'error';
      return { m3: m3Diff.toFixed(3), liter: Math.round(m3Diff * 1000) };
    }
    if (isGas) {
      // Gas: m³_diff × Brennwert × z-Zahl
      if (!form.m3_stand || !lastEntry?.row_data?.m3_stand) return null;
      const cfg = getLocConfig(dialogLocId);
      const brennwert = cfg?.brennwert || module?.meter_config?.default_brennwert || 10.0;
      const zZahl = cfg?.z_zahl || module?.meter_config?.default_z_zahl || 0.964;
      const m3Diff = parseFloat(form.m3_stand) - parseFloat(lastEntry.row_data.m3_stand || 0);
      if (m3Diff < 0) return 'error';
      return { kwh: Math.round(m3Diff * brennwert * zZahl), m3: m3Diff.toFixed(3) };
    }
    // Strom: (HT_diff + NT_diff) × Faktor
    if (!form.ht_stand || !lastEntry?.row_data?.ht_stand) return null;
    const cfg = getLocConfig(dialogLocId);
    const factor = cfg?.factor || module?.meter_config?.default_factor || 1;
    const htDiff = parseFloat(form.ht_stand) - parseFloat(lastEntry.row_data.ht_stand || 0);
    if (htDiff < 0) return 'error';
    const hasNtLocal = getHasNt(dialogLocId);
    const ntDiff = (hasNtLocal && form.nt_stand && lastEntry.row_data.nt_stand != null)
      ? parseFloat(form.nt_stand || 0) - parseFloat(lastEntry.row_data.nt_stand || 0)
      : 0;
    return Math.round((htDiff + ntDiff) * factor);
  }, [form.ht_stand, form.nt_stand, form.m3_stand, dialogLocId, lastEntry, module, getLocConfig, getHasNt, isMeterReset, isGas, isWater]);

  const openDialog = async (locId) => {
    const singleLoc = locations.length === 1 ? locations[0].id : null;
    const loc = locId || (!isAdmin ? user?.location_ids?.[0] : null) || singleLoc || '';
    setEditEntryId(null);
    setIsMeterReset(false);
    setNewMeterNr('');
    setNewMeterFaktor('');
    setDialogLocId(loc);
    setForm({ ht_stand: '', nt_stand: '', m3_stand: '', reading_date: new Date().toISOString().slice(0, 10) });
    setPhoto(null);
    setPhotoPreview(null);
    setSelectedZaehlerId('');
    if (loc) {
      try {
        const res = await axios.get(`${API}/api/info/${moduleId}/entries`, {
          headers, params: { location_id: loc, limit: 1 },
        });
        setLastEntry((res.data || [])[0] || null);
      } catch { setLastEntry(null); }
    } else { setLastEntry(null); }
    setShowDialog(true);
  };

  const openEditDialog = async (entry) => {
    const rd = entry.row_data || {};
    setEditEntryId(entry.id);
    setIsMeterReset(!!rd.is_meter_reset);
    setDialogLocId(entry.location_id);
    setForm({
      ht_stand: rd.ht_stand != null ? String(rd.ht_stand) : '',
      nt_stand: rd.nt_stand != null ? String(rd.nt_stand) : '',
      m3_stand: rd.m3_stand != null ? String(rd.m3_stand) : '',
      reading_date: rd.reading_date || entry.created_at?.slice(0, 10) || new Date().toISOString().slice(0, 10),
    });
    setPhoto(null);
    setPhotoPreview(null);
    // Fetch the PREVIOUS entry (for recalculation preview)
    try {
      const res = await axios.get(`${API}/api/info/${moduleId}/entries`, {
        headers, params: { location_id: entry.location_id, limit: 10 },
      });
      const all = res.data || [];
      const idx = all.findIndex(e => e.id === entry.id);
      setLastEntry(idx < all.length - 1 ? all[idx + 1] : null);
    } catch { setLastEntry(null); }
    setShowDialog(true);
  };

  const handleDialogLocChange = async (locId) => {
    setDialogLocId(locId);
    setLastEntry(null);
    setSelectedZaehlerId(''); // Zähler-Auswahl zurücksetzen bei Standort-Wechsel
    if (locId) {
      try {
        const res = await axios.get(`${API}/api/info/${moduleId}/entries`, {
          headers, params: { location_id: locId, limit: 1 },
        });
        setLastEntry((res.data || [])[0] || null);
      } catch {}
    }
  };

  const handlePhotoChange = (file) => {
    if (!file) return;
    setPhoto(file);
    const reader = new FileReader();
    reader.onload = (e) => setPhotoPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    const locId = dialogLocId || user?.location_ids?.[0];
    if (!locId) { toast.error('Bitte Standort auswählen'); return; }

    if (isGas) {
      if (!form.m3_stand) { toast.error('m³-Stand ist Pflichtfeld'); return; }
    } else if (isWater) {
      if (!selectedZaehlerId) { toast.error('Bitte einen Zähler auswählen'); return; }
      if (!form.m3_stand) { toast.error('m³-Stand ist Pflichtfeld'); return; }
    } else {
      if (!form.ht_stand) { toast.error('HT-Stand ist Pflichtfeld'); return; }
      // Zählerwechsel: neue Zählernummer Pflicht
      if (isMeterReset && !isWater && !newMeterNr.trim()) {
        toast.error('Bitte neue Zählernummer eintragen (Pflicht bei Zählerwechsel)');
        return;
      }
      const requirePhoto = module?.meter_config?.require_photo !== false;
      if (requirePhoto && !photo && !editEntryId) { toast.error('Foto des Zählers ist Pflicht'); return; }
    }

    setSaving(true);
    try {
      const rowData = isGas
        ? {
            reading_date: form.reading_date,
            m3_stand: parseFloat(form.m3_stand),
            ...(isMeterReset ? { is_meter_reset: true } : {}),
          }
        : isWater
        ? {
            reading_date: form.reading_date,
            m3_stand: parseFloat(form.m3_stand),
            zaehler_config_id: selectedZaehlerId,
            ...(isMeterReset ? { is_meter_reset: true } : {}),
          }
        : {
            reading_date: form.reading_date,
            ht_stand: parseFloat(form.ht_stand),
            nt_stand: form.nt_stand ? parseFloat(form.nt_stand) : null,
            ...(isMeterReset ? {
              is_meter_reset: true,
              ...(newMeterNr.trim() ? { new_meter_nr: newMeterNr.trim() } : {}),
              ...(newMeterFaktor ? { new_meter_faktor: parseFloat(newMeterFaktor) } : {}),
            } : {}),
          };

      const payload = { location_id: locId, row_data: rowData };

      let entryId;
      if (editEntryId) {
        // Bearbeiten (PATCH)
        const res = await axios.patch(`${API}/api/info/${moduleId}/entries/${editEntryId}`, payload, { headers });
        entryId = editEntryId;
        const rd = res.data?.row_data || {};
        toast.success(
          `Eintrag aktualisiert – Verbrauch: ${fmtKwh(rd.verbrauch_kwh)}`,
          { description: 'Achtung: Nachfolgende Ablesungen basieren noch auf den alten Werten.' }
        );
      } else {
        // Neu anlegen (POST)
        const res = await axios.post(`${API}/api/info/${moduleId}/entries`, payload, { headers });
        entryId = res.data.id;
        const rd = res.data.row_data || {};
        if (isMeterReset) {
          toast.success('Zählerwechsel gespeichert – neue Startwerte gesetzt');
          // Konfiguration automatisch mit neuer Zählernummer aktualisieren
          if (newMeterNr.trim() && !isWater) {
            try {
              const modConfig = module?.meter_config || {};
              const locConfigs = (module?.location_meter_configs || []).map(c =>
                c.location_id === locId
                  ? {
                      ...c,
                      zaehler_nr: newMeterNr.trim(),
                      ...(newMeterFaktor ? { faktor_c: parseFloat(newMeterFaktor) } : {}),
                    }
                  : c
              );
              await axios.put(`${API}/api/info/${moduleId}/meter-location-configs`, {
                meter_config: modConfig,
                location_meter_configs: locConfigs,
              }, { headers });
              toast.success(
                `Konfiguration aktualisiert – Zähler ${newMeterNr.trim()} ist jetzt aktiv`,
                { description: isGas ? '' : newMeterFaktor ? `Neuer Faktor C: ${newMeterFaktor}` : '' }
              );
            } catch {
              toast.warning('Ablesung gespeichert, aber Konfiguration konnte nicht automatisch aktualisiert werden. Bitte manuell anpassen.');
            }
          }
        } else if (rd.is_first_reading) {
          toast.success('Erstablesung gespeichert – Verbrauch ab nächstem Eintrag berechnet');
        } else if (rd.anomaly_flag) {
          const msg = isGas
            ? `Ablesung gespeichert – ⚠ ${fmtM3(rd.verbrauch_m3, 3)} (~${fmtKwh(rd.verbrauch_kwh)}) Anomalie!`
            : `Ablesung gespeichert – ⚠ Verbrauch ${fmtKwh(rd.verbrauch_kwh)} (Anomalie: ${rd.anomaly_pct}% über Ø)`;
          toast.warning(msg);
        } else {
          const msg = isGas
            ? `Gespeichert: ${fmtM3(rd.verbrauch_m3, 3)} (~ ${fmtKwh(rd.verbrauch_kwh)}) erfasst.`
            : isWater
            ? `Gespeichert: ${fmtM3(rd.verbrauch_m3, 3)} (${rd.verbrauch_liter != null ? Number(rd.verbrauch_liter).toLocaleString('de-DE') + ' L' : '–'}) erfasst.`
            : `Ablesung gespeichert – Verbrauch: ${fmtKwh(rd.verbrauch_kwh)}`;
          toast.success(msg);
        }
      }

      // Foto hochladen (nur bei Neu-Anlage oder explizit neues Foto)
      if (photo && entryId) {
        const fd = new FormData();
        fd.append('photo', photo);
        await axios.post(`${API}/api/info/${moduleId}/entries/${entryId}/photo`, fd, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        });
      }

      setShowDialog(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eintrag löschen?')) return;
    try {
      await axios.delete(`${API}/api/info/${moduleId}/entries/${id}`, { headers });
      toast.success('Gelöscht');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const buildExportParams = () => {
    const p = new URLSearchParams();
    if (selectedLocation) p.set('location_id', selectedLocation);
    if (dateFrom) p.set('date_from', dateFrom);
    if (dateTo) p.set('date_to', dateTo);
    return p.toString();
  };

  const handleExportCsv = async () => {
    try {
      const qs = buildExportParams();
      const res = await axios.get(`${API}/api/info/${moduleId}/export-csv${qs ? `?${qs}` : ''}`, {
        headers, responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url;
      a.download = `${module?.name || 'strom'}_export.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Fehler beim CSV-Export'); }
  };

  const handleExportPdf = async () => {
    const qs = buildExportParams();
    try {
      const res = await axios.get(`${API}/api/info/${moduleId}/export-pdf${qs ? `?${qs}` : ''}`, {
        headers, responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url;
      const fileLabel = dateFrom || dateTo ? `${dateFrom || 'anfang'}_${dateTo || 'heute'}` : 'gesamt';
      a.download = `ableseprotokoll_${fileLabel}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Ableseprotokoll heruntergeladen');
    } catch { toast.error('Fehler beim PDF-Export'); }
  };

  // Config speichern
  const openConfig = () => {
    setCfgForm({
      meter_config: { ...(module?.meter_config || {}) },
      location_meter_configs: locations.map(loc => {
        const existing = (module?.location_meter_configs || []).find(c => c.location_id === loc.id);
        return {
          location_id: loc.id,
          zaehler_nr: existing?.zaehler_nr || existing?.zaehler_nr_ht || '',
          has_nt: existing?.has_nt === true || !!existing?.zaehler_nr_nt,
          factor: existing?.factor || module?.meter_config?.default_factor || 1,
        };
      }),
    });
    setShowConfig(true);
  };

  const updateLocCfg = (locId, field, value) =>
    setCfgForm(f => ({
      ...f,
      location_meter_configs: f.location_meter_configs.map(c =>
        c.location_id === locId ? { ...c, [field]: value } : c
      ),
    }));

  const handleSaveConfig = async () => {
    // Pflichtfeld-Validierung: HT Zählernummer
    const missing = cfgForm.location_meter_configs.find(c => !c.zaehler_nr?.trim());
    if (missing) {
      toast.error(`Bitte Zählernummer für Standort "${locName(missing.location_id)}" eintragen`);
      return;
    }
    setSavingCfg(true);
    try {
      await axios.put(`${API}/api/info/${moduleId}/meter-location-configs`, cfgForm, { headers });
      toast.success('Konfiguration gespeichert');
      setShowConfig(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    } finally {
      setSavingCfg(false);
    }
  };

  // Stats
  const thisMonthKwh = useMemo(() => {
    const now = new Date();
    const monat = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const m = monthlyStats.find(s => s.monat === monat);
    return m?.verbrauch_kwh || null;
  }, [monthlyStats]);

  const anomalyCount = entries.filter(e => e.row_data?.anomaly_flag).length;
  const lastReading = entries[0];

  // Ø/Tag on-the-fly: Vorgänger nach reading_date (nur für diese Anzeige)
  const avgPerDay = useMemo(() => {
    const groups = {};
    entries.forEach(e => {
      const rd = e.row_data || {};
      const key = isWater ? `${e.location_id}|${rd.zaehler_config_id || ''}` : e.location_id;
      (groups[key] = groups[key] || []).push(e);
    });
    const map = {};
    Object.values(groups).forEach(grp => {
      const sorted = [...grp].sort((a, b) => {
        const da = (a.row_data?.reading_date || a.created_at || '').slice(0, 10);
        const db = (b.row_data?.reading_date || b.created_at || '').slice(0, 10);
        return da < db ? -1 : da > db ? 1 : 0;
      });
      let prev = null;
      sorted.forEach(e => {
        const rd = e.row_data || {};
        const verbrauch = isWater ? rd.verbrauch_m3 : rd.verbrauch_kwh;
        const curStr = (rd.reading_date || e.created_at || '').slice(0, 10);
        const cur = curStr ? new Date(curStr) : null;
        let avg = null;
        if (prev && cur && verbrauch != null && !rd.is_first_reading && !rd.is_meter_reset) {
          const days = Math.round((cur - prev) / 86400000);
          if (days > 0) avg = verbrauch / days;
        }
        map[e.id] = avg;
        if (cur) prev = cur;
      });
    });
    return map;
  }, [entries, isWater]);

  const fmtAvg = (v) => {
    if (v == null) return '–';
    return isWater
      ? `${Number(v).toLocaleString('de-DE', { maximumFractionDigits: 3 })} m³/Tag`
      : `${Number(v).toLocaleString('de-DE', { maximumFractionDigits: 1 })} kWh/Tag`;
  };

  if (loading) return (
    <DashboardLayout>
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    </DashboardLayout>
  );

  if (!module) return (
    <DashboardLayout>
      <div className="p-6 text-center text-muted-foreground">Modul nicht gefunden</div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-5 p-4 md:p-6" data-testid="info-meter-page">
        {/* ── Header ─────────────────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${moduleBg}`}>
              <ModuleIcon className={`h-5 w-5 ${moduleColor}`} />
            </div>
            <div>
              <h1 className="text-xl font-bold">{module.name}</h1>
              <p className="text-sm text-muted-foreground">Zähler-Verwaltung · Standort-isoliert</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {isAdmin && (
              <select
                value={selectedLocation}
                onChange={e => setSelectedLocation(e.target.value)}
                className="h-9 rounded-md border bg-background px-3 text-sm"
                data-testid="location-filter-select"
              >
                <option value="">Alle Standorte</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            )}
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={openConfig} data-testid="meter-config-btn">
                <Settings className="h-4 w-4 mr-1" /> Konfiguration
              </Button>
            )}
            {isAdmin && (
              <>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={e => setDateFrom(e.target.value)}
                  className="h-9 rounded-md border bg-background px-2 text-sm"
                  title="Von-Datum"
                  data-testid="export-date-from"
                />
                <span className="self-center text-sm text-muted-foreground">–</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={e => setDateTo(e.target.value)}
                  className="h-9 rounded-md border bg-background px-2 text-sm"
                  title="Bis-Datum"
                  data-testid="export-date-to"
                />
                {(dateFrom || dateTo) && (
                  <Button variant="ghost" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); }}
                    data-testid="export-date-clear">
                    Zurücksetzen
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={handleExportCsv} data-testid="export-csv-btn">
                  <FileDown className="h-4 w-4 mr-1" /> CSV
                </Button>
                <Button variant="outline" size="sm" onClick={handleExportPdf} data-testid="export-pdf-btn">
                  <FileDown className="h-4 w-4 mr-1" /> PDF
                </Button>
              </>
            )}
            <Button size="sm" onClick={() => openDialog(selectedLocation || '')} data-testid="add-reading-btn">
              <Plus className="h-4 w-4 mr-1" /> Neue Ablesung
            </Button>
          </div>
        </div>

        {/* ── Stats-Kacheln ──────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Card>
            <CardContent className="py-3 px-4">
              <div className="text-xs text-muted-foreground mb-1">Aktueller Monat</div>
              <div className="text-xl font-bold text-blue-600">{fmtKwh(thisMonthKwh)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-3 px-4">
              <div className="text-xs text-muted-foreground mb-1">Letzte Ablesung</div>
              <div className="text-sm font-medium">
                {lastReading ? fmtDate(lastReading.row_data?.reading_date || lastReading.created_at) : '–'}
              </div>
              <div className="text-xs text-muted-foreground">{lastReading?.created_by_name || '–'}</div>
            </CardContent>
          </Card>
          <Card className={`col-span-2 md:col-span-1 ${anomalyCount > 0 ? 'border-orange-300 bg-orange-50 dark:bg-orange-900/10' : ''}`}>
            <CardContent className="py-3 px-4">
              <div className="text-xs text-muted-foreground mb-1">Anomalien</div>
              <div className={`text-xl font-bold ${anomalyCount > 0 ? 'text-orange-500' : 'text-green-600'}`}>
                {anomalyCount > 0 ? `⚠ ${anomalyCount}` : '✓ Keine'}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ── Sparkline ──────────────────────────────────────────────────────── */}
        {monthlyStats.length > 1 && (
          <Card>
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm font-medium text-muted-foreground">Verbrauchsverlauf (kWh)</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <ResponsiveContainer width="100%" height={100}>
                <LineChart data={monthlyStats} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="monat" tick={{ fontSize: 10 }} tickFormatter={v => v.slice(5)} />
                  <YAxis tick={{ fontSize: 10 }} width={50}
                    tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                  <Tooltip formatter={(v) => [`${Number(v).toLocaleString('de-DE')} kWh`, 'Verbrauch']}
                    labelFormatter={(l) => `Monat: ${l}`} />
                  <Line type="monotone" dataKey="verbrauch_kwh" stroke="#2563eb" strokeWidth={2}
                    dot={{ r: 3, fill: '#2563eb' }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* ── Tabelle ────────────────────────────────────────────────────────── */}
        <Card>
          <CardContent className="p-0">
            {entries.length === 0 ? (
              <div className="py-16 text-center">
                <ModuleIcon className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground">Noch keine Ablesungen.</p>
                <Button className="mt-3" size="sm" onClick={() => openDialog('')}>
                  <Plus className="h-4 w-4 mr-1" /> Erste Ablesung eintragen
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/40">
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Datum</th>
                      {isAdmin && <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Standort</th>}
                      {isGas ? (
                        <>
                          <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">m³-Stand</th>
                          <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Δ m³</th>
                        </>
                      ) : isWater ? (
                        <>
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Zähler</th>
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Typ</th>
                          <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">m³-Stand</th>
                          <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Δ (m³ / Liter)</th>
                        </>
                      ) : (
                        <>
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Zähler-Nr</th>
                          <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">HT-Stand</th>
                          <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">NT-Stand</th>
                        </>
                      )}
                      {!isWater && (
                        <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Verbrauch kWh</th>
                      )}
                      <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground" data-testid="col-avg-per-day">Ø/Tag</th>
                      <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Δ % Ø</th>
                      <th className="text-center px-2 py-2.5 text-xs font-medium text-muted-foreground">Foto</th>
                      <th className="w-8 px-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((entry, idx) => {
                      const rd = entry.row_data || {};
                      const cfg = getLocConfig(entry.location_id);
                      return (
                        <tr key={entry.id} className={`border-b last:border-0 hover:bg-muted/20 transition-colors ${idx % 2 !== 0 ? 'bg-muted/10' : ''}`}>
                          <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">
                            {fmtDate(rd.reading_date || entry.created_at)}
                          </td>
                          {isAdmin && (
                            <td className="px-4 py-2.5">
                              <Badge variant="outline" className="text-xs font-normal">{locName(entry.location_id)}</Badge>
                            </td>
                          )}
                          {isGas ? (
                            <>
                              <td className="px-4 py-2.5 text-right font-mono text-sm">
                                {rd.m3_stand != null ? `${Number(rd.m3_stand).toLocaleString('de-DE')} m³` : '–'}
                              </td>
                              <td className="px-4 py-2.5 text-right font-mono text-sm text-muted-foreground">
                                {rd.verbrauch_m3 != null ? `${Number(rd.verbrauch_m3).toLocaleString('de-DE', { maximumFractionDigits: 3 })} m³` : '–'}
                              </td>
                            </>
                          ) : isWater ? (
                            <>
                              <td className="px-4 py-2.5 text-sm">
                                <div className="font-medium">{rd.zaehler_name || '–'}</div>
                              </td>
                              <td className="px-4 py-2.5">
                                <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                                  rd.zaehler_typ === 'zwischen'
                                    ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                                    : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                }`}>
                                  {rd.zaehler_typ === 'zwischen' ? 'Zwischen' : 'Haupt'}
                                </span>
                              </td>
                              <td className="px-4 py-2.5 text-right font-mono text-sm">
                                {rd.m3_stand != null ? `${Number(rd.m3_stand).toLocaleString('de-DE')} m³` : '–'}
                              </td>
                              <td className="px-4 py-2.5 text-right font-mono text-sm text-muted-foreground">
                                {rd.verbrauch_m3 != null
                                  ? `${Number(rd.verbrauch_m3).toLocaleString('de-DE', { maximumFractionDigits: 3 })} m³ (${Number(rd.verbrauch_liter || rd.verbrauch_m3 * 1000).toLocaleString('de-DE', { maximumFractionDigits: 0 })} L)`
                                  : '–'}
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-4 py-2.5 text-xs font-mono text-muted-foreground">
                                {getZaehlerNr(entry.location_id) || '–'}
                              </td>
                              <td className="px-4 py-2.5 text-right font-mono text-sm">
                                {rd.ht_stand != null ? Number(rd.ht_stand).toLocaleString('de-DE') : '–'}
                              </td>
                              <td className="px-4 py-2.5 text-right font-mono text-sm text-muted-foreground">
                                {rd.nt_stand != null ? Number(rd.nt_stand).toLocaleString('de-DE') : '–'}
                              </td>
                            </>
                          )}
                          {!isWater && (
                            <td className="px-4 py-2.5 text-right">
                              {rd.is_meter_reset ? (
                                <Badge variant="outline" className="text-xs border-blue-300 text-blue-600">Zählerwechsel</Badge>
                              ) : rd.is_first_reading ? (
                                <Badge variant="secondary" className="text-xs">Erstablesung</Badge>
                              ) : (
                                <span className="font-bold text-blue-600 text-sm">{fmtKwh(rd.verbrauch_kwh)}</span>
                              )}
                            </td>
                          )}
                          <td className="px-4 py-2.5 text-right font-mono text-xs text-muted-foreground" data-testid={`avg-per-day-${entry.id}`}>
                            {fmtAvg(avgPerDay[entry.id])}
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <DeltaBadge pct={rd.anomaly_pct} isAnomaly={rd.anomaly_flag} />
                          </td>
                          <td className="px-2 py-2.5 text-center">
                            {entry.photo_path ? (
                              <a
                                href={`${API}/api/info/${moduleId}/entries/${entry.id}/photo?token=${localStorage.getItem('token')}`}
                                target="_blank" rel="noopener noreferrer"
                                className="text-blue-500 hover:text-blue-700"
                              >
                                <Image className="h-4 w-4" />
                              </a>
                            ) : <span className="text-muted-foreground/30">–</span>}
                          </td>
                          <td className="px-2 py-2.5">
                            <div className="flex gap-0.5">
                              {canEdit && (
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground"
                                  onClick={() => openEditDialog(entry)} data-testid={`edit-entry-${entry.id}`}>
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              {canDelete && (
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                                  onClick={() => handleDelete(entry.id)} data-testid={`delete-entry-${entry.id}`}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Neue Ablesung Dialog ────────────────────────────────────────────── */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editEntryId
                ? <><Pencil className="h-4 w-4" /> Eintrag bearbeiten – {module.name}</>
                : <><ModuleIcon className={`h-4 w-4 ${moduleColor}`} /> Neue Ablesung – {module.name}</>
              }
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-1">
            {/* Standort (Admin, mehrere Standorte) */}
            {isAdmin && locations.length > 1 && !editEntryId && (
              <div className="space-y-1">
                <label className="text-sm font-medium">Standort *</label>
                <select
                  value={dialogLocId}
                  onChange={e => handleDialogLocChange(e.target.value)}
                  className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                  data-testid="dialog-location-select"
                >
                  <option value="">Bitte wählen…</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
            )}

            {/* Strom: Zählernummer-Anzeige (prominent) */}
            {!isGas && dialogLocId && getZaehlerNr(dialogLocId) && (
              <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-3 py-2.5">
                <div className="text-xs text-blue-600 font-medium mb-0.5">Bitte ablesen:</div>
                <div className="font-bold text-base text-blue-800 dark:text-blue-300 font-mono">
                  Zähler {getZaehlerNr(dialogLocId)}
                </div>
                {getHasNt(dialogLocId) && (
                  <div className="text-xs text-blue-500 mt-0.5">Register 1.8.1 (HT) und 1.8.2 (NT) ablesen</div>
                )}
              </div>
            )}

            {/* Zählerwechsel-Checkbox (Admin only) */}
            {canEdit && !editEntryId && (
              <div
                className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${isMeterReset ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/10' : ''}`}
                onClick={() => setIsMeterReset(v => !v)}
              >
                <div className={`mt-0.5 h-4 w-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${isMeterReset ? 'bg-amber-500 border-amber-500' : 'border-muted-foreground'}`}>
                  {isMeterReset && <span className="text-white text-xs leading-none">✓</span>}
                </div>
                <div>
                  <div className="text-sm font-medium flex items-center gap-1.5">
                    <RefreshCw className="h-3.5 w-3.5" /> Zählerwechsel
                  </div>
                  <div className="text-xs text-muted-foreground">Neue Startwerte setzen (kein Verbrauch für diesen Eintrag)</div>
                </div>
              </div>
            )}
            {isMeterReset && editEntryId && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700">
                <RefreshCw className="h-3.5 w-3.5 inline mr-1" /> Dieser Eintrag ist als Zählerwechsel markiert.
              </div>
            )}

            {/* Neue Zählernummer + Faktor bei Zählerwechsel (nur Neu-Anlage, nicht Wasser) */}
            {isMeterReset && !editEntryId && !isWater && (
              <div className="rounded-xl border-2 border-amber-300 bg-amber-50 dark:bg-amber-900/10 p-4 space-y-3">
                <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
                  <RefreshCw className="h-3.5 w-3.5" />
                  Neuer Zähler – Angaben zum Austausch
                </p>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-foreground">
                    Neue Zählernummer / Seriennummer <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="text"
                    value={newMeterNr}
                    onChange={e => setNewMeterNr(e.target.value)}
                    placeholder="z.B. 12345678"
                    autoFocus
                    className="w-full h-10 rounded-lg border-2 border-amber-300 bg-white dark:bg-gray-800 px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-400"
                    data-testid="new-meter-nr-input"
                  />
                  <p className="text-xs text-amber-600 dark:text-amber-500">
                    Steht auf dem Zählergehäuse. Wird direkt in die Konfiguration übernommen.
                  </p>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-foreground">
                    Neuer Faktor C (Wandlerfaktor)
                    <span className="text-muted-foreground text-xs ml-1">(optional – nur wenn geändert)</span>
                  </label>
                  <input
                    type="number"
                    value={newMeterFaktor}
                    onChange={e => setNewMeterFaktor(e.target.value)}
                    placeholder={`Aktuell: ${getLocConfig(dialogLocId)?.faktor_c || module?.meter_config?.default_faktor_c || '–'}`}
                    min="1"
                    className="w-full h-10 rounded-lg border border-amber-200 bg-white dark:bg-gray-800 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-amber-400"
                    data-testid="new-meter-faktor-input"
                  />
                </div>
              </div>
            )}

            {/* Wasser: Zähler-Auswahl */}
            {isWater && (
              <div className="space-y-1">
                <label className="text-sm font-medium">Zähler auswählen *</label>
                {getWaterMeters(dialogLocId || user?.location_ids?.[0]).length === 0 ? (
                  <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    Noch keine Zähler konfiguriert. Bitte zuerst unter "Konfiguration" Zähler anlegen.
                  </div>
                ) : (
                  <select
                    value={selectedZaehlerId}
                    onChange={async e => {
                      const zid = e.target.value;
                      setSelectedZaehlerId(zid);
                      setLastEntry(null);
                      if (zid && (dialogLocId || user?.location_ids?.[0])) {
                        try {
                          const locId = dialogLocId || user?.location_ids?.[0];
                          const res = await axios.get(`${API}/api/info/${moduleId}/entries`, {
                            headers,
                            params: { location_id: locId, limit: 50 },
                          });
                          const all = res.data || [];
                          const last = all.find(e => e.row_data?.zaehler_config_id === zid);
                          setLastEntry(last || null);
                        } catch {}
                      }
                    }}
                    className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                    data-testid="water-meter-select"
                  >
                    <option value="">Zähler wählen…</option>
                    {getWaterMeters(dialogLocId || user?.location_ids?.[0]).map(m => (
                      <option key={m.id} value={m.id}>
                        {m.name} ({m.zaehler_typ === 'zwischen' ? 'Zwischenzähler' : 'Hauptzähler'})
                        {m.zaehler_nr ? ` · ${m.zaehler_nr}` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* Letzter Wert (Vorschlag) */}
            {!isMeterReset && isWater && lastEntry?.row_data?.m3_stand != null && (
              <div className="text-xs text-muted-foreground bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded px-3 py-1.5">
                Letzter m³-Stand ({lastEntry.row_data.zaehler_name || 'Zähler'}):
                <span className="font-mono font-medium ml-1">{Number(lastEntry.row_data.m3_stand).toLocaleString('de-DE')} m³</span>
                <span className="ml-3 text-muted-foreground/60">({fmtDate(lastEntry.row_data.reading_date || lastEntry.created_at)})</span>
              </div>
            )}
            {!isMeterReset && isGas && lastEntry?.row_data?.m3_stand != null && (
              <div className="text-xs text-muted-foreground bg-muted/40 rounded px-3 py-1.5">
                Letzter m³-Stand: <span className="font-mono font-medium">{Number(lastEntry.row_data.m3_stand).toLocaleString('de-DE')}</span>
                <span className="ml-3 text-muted-foreground/60">({fmtDate(lastEntry.row_data.reading_date || lastEntry.created_at)})</span>
              </div>
            )}
            {!isMeterReset && !isGas && lastEntry?.row_data?.ht_stand && (
              <div className="text-xs text-muted-foreground bg-muted/40 rounded px-3 py-1.5">
                Letzter HT-Stand: <span className="font-mono font-medium">{Number(lastEntry.row_data.ht_stand).toLocaleString('de-DE')}</span>
                {lastEntry.row_data.nt_stand != null && (
                  <span className="ml-3">NT: <span className="font-mono font-medium">{Number(lastEntry.row_data.nt_stand).toLocaleString('de-DE')}</span></span>
                )}
                <span className="ml-3 text-muted-foreground/60">({fmtDate(lastEntry.row_data.reading_date || lastEntry.created_at)})</span>
              </div>
            )}

            {/* Datum */}
            <div className="space-y-1">
              <label className="text-sm font-medium">Ablesedatum</label>
              <Input type="date" value={form.reading_date}
                onChange={e => setForm(f => ({ ...f, reading_date: e.target.value }))}
                data-testid="reading-date-input" />
            </div>

            {/* Gas / Wasser: m³-Stand */}
            {(isGas || isWater) ? (
              <div className="space-y-1">
                <label className="text-sm font-medium">
                  {isWater ? 'Zählerstand (m³) *' : 'Zählerstand (m³) *'}
                </label>
                <Input
                  type="number" step={isWater ? "0.001" : "0.001"}
                  value={form.m3_stand}
                  onChange={e => setForm(f => ({ ...f, m3_stand: e.target.value }))}
                  placeholder={lastEntry?.row_data?.m3_stand != null
                    ? `Vorheriger Wert: ${Number(lastEntry.row_data.m3_stand).toLocaleString('de-DE')}`
                    : 'z.B. 12345.678'}
                  data-testid="m3-stand-input"
                  className="text-lg font-mono"
                  autoFocus
                />
              </div>
            ) : (
              <>
                {/* Strom: HT-Stand */}
                <div className="space-y-1">
                  <label className="text-sm font-medium">
                    HT-Stand (1.8.1) *
                    {getLocConfig(dialogLocId)?.zaehler_nr_ht &&
                      <span className="text-xs text-muted-foreground ml-1 font-normal font-mono">
                        Nr. {getLocConfig(dialogLocId).zaehler_nr_ht}
                      </span>}
                  </label>
                  <Input type="number" step="0.01" value={form.ht_stand}
                    onChange={e => setForm(f => ({ ...f, ht_stand: e.target.value }))}
                    placeholder="z.B. 8234.50" data-testid="ht-stand-input" />
                </div>

                {/* Strom: NT-Stand */}
                {dialogLocId && getHasNt(dialogLocId) && (
                  <div className="space-y-1">
                    <label className="text-sm font-medium">NT-Stand (1.8.2)</label>
                    <Input type="number" step="0.01" value={form.nt_stand}
                      onChange={e => setForm(f => ({ ...f, nt_stand: e.target.value }))}
                      placeholder="z.B. 4112.20" data-testid="nt-stand-input" />
                  </div>
                )}
              </>
            )}

            {/* Live-Vorschau */}
            {previewKwh !== null && (
              <div className={`rounded-lg px-3 py-2 text-sm font-medium text-center ${
                previewKwh === 'error'
                  ? 'bg-red-50 text-red-600 border border-red-200'
                  : isGas
                    ? 'bg-orange-50 text-orange-700 border border-orange-200 dark:bg-orange-900/20 dark:text-orange-300'
                    : 'bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/20 dark:text-blue-300'
              }`}>
                {previewKwh === 'error'
                  ? '⚠ Stand kleiner als Vorwert – bitte Zählerwechsel markieren'
                  : isGas
                    ? <>🔥 ~{fmtM3(previewKwh.m3, 3)} = <span className="font-bold">{Number(previewKwh.kwh).toLocaleString('de-DE')} kWh</span></>
                    : isWater
                    ? <>💧 <span className="font-bold">{Number(previewKwh.m3).toLocaleString('de-DE', { maximumFractionDigits: 3 })} m³</span> = <span className="font-medium">{Number(previewKwh.liter).toLocaleString('de-DE')} L</span></>
                    : <>⚡ Voraussichtlicher Verbrauch: <span className="font-bold">{Number(previewKwh).toLocaleString('de-DE')} kWh</span></>
                }
              </div>
            )}
            {!lastEntry && dialogLocId && (
              <div className="text-xs text-muted-foreground bg-amber-50 border border-amber-200 rounded px-3 py-2">
                Erstablesung – kein Verbrauch berechnet (wird als Basiswert gespeichert)
              </div>
            )}

            {/* Foto-Upload (Pflicht bei Strom, optional bei Gas) */}
            <div className="space-y-1">
              <label className="text-sm font-medium">
                Foto des Zählers {!isGas && module?.meter_config?.require_photo !== false ? '*' : <span className="text-muted-foreground font-normal">(optional)</span>}
              </label>
              <input ref={photoRef} type="file" accept="image/*"
                className="hidden" onChange={e => handlePhotoChange(e.target.files[0])} />
              {photoPreview ? (
                <div className="relative rounded-lg overflow-hidden border">
                  <img src={photoPreview} alt="Vorschau" className="w-full h-32 object-cover" />
                  <button type="button" onClick={() => { setPhoto(null); setPhotoPreview(null); }}
                    className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-1 text-xs">✕</button>
                </div>
              ) : (
                <button type="button" onClick={() => photoRef.current?.click()}
                  className="w-full h-20 border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-1 text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                  data-testid="photo-upload-btn">
                  <Camera className="h-6 w-6" />
                  <span className="text-xs">Foto aufnehmen / auswählen</span>
                </button>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Abbrechen</Button>
            <Button onClick={handleSubmit} disabled={saving} data-testid="confirm-reading-btn">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Speichern…</> : 'Ablesung speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Konfiguration Dialog ────────────────────────────────────────────── */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-4 w-4" /> Zähler-Konfiguration – {module.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Wasser: CRUD für mehrere Zähler pro Standort */}
            {isWater ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold">Zähler pro Standort</label>
                  <Button size="sm" variant="outline" onClick={() => {
                    const newId = `w-${Date.now()}`;
                    const firstLocId = locations[0]?.id || '';
                    setCfgForm(f => ({
                      ...f,
                      location_meter_configs: [
                        ...(f.location_meter_configs || []),
                        { id: newId, location_id: firstLocId, name: '', zaehler_nr: '', zaehler_typ: 'haupt' }
                      ]
                    }));
                  }}>
                    <Plus className="h-3.5 w-3.5 mr-1" /> Zähler hinzufügen
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Jede Station kann mehrere Zähler haben (z.B. Hauptzähler + Waschanlage). Zwischenzähler sind abwasserbefreit.
                </p>
                {(cfgForm.location_meter_configs || []).length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-4 border rounded-lg border-dashed">
                    Noch keine Zähler konfiguriert. Klicke "Zähler hinzufügen".
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(cfgForm.location_meter_configs || []).map((cfg, idx) => (
                      <div key={cfg.id || idx} className="border rounded-lg p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-muted-foreground">Zähler {idx + 1}</span>
                          <button
                            onClick={() => setCfgForm(f => ({
                              ...f,
                              location_meter_configs: f.location_meter_configs.filter((_, i) => i !== idx)
                            }))}
                            className="text-destructive hover:text-destructive/80"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1 col-span-2">
                            <label className="text-xs text-muted-foreground">Standort *</label>
                            <select
                              value={cfg.location_id || ''}
                              onChange={e => setCfgForm(f => ({
                                ...f,
                                location_meter_configs: f.location_meter_configs.map((c, i) =>
                                  i === idx ? { ...c, location_id: e.target.value } : c
                                )
                              }))}
                              className="h-8 w-full rounded border bg-background px-2 text-xs"
                            >
                              <option value="">Standort wählen</option>
                              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                            </select>
                          </div>
                          <div className="space-y-1 col-span-2">
                            <label className="text-xs text-muted-foreground">Bezeichnung * (z.B. "Hauptzähler Keller")</label>
                            <Input
                              value={cfg.name || ''}
                              onChange={e => setCfgForm(f => ({
                                ...f,
                                location_meter_configs: f.location_meter_configs.map((c, i) =>
                                  i === idx ? { ...c, name: e.target.value } : c
                                )
                              }))}
                              placeholder="z.B. Hauptzähler Keller"
                              className="h-8 text-xs"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs text-muted-foreground">Zählernummer</label>
                            <Input
                              value={cfg.zaehler_nr || ''}
                              onChange={e => setCfgForm(f => ({
                                ...f,
                                location_meter_configs: f.location_meter_configs.map((c, i) =>
                                  i === idx ? { ...c, zaehler_nr: e.target.value } : c
                                )
                              }))}
                              placeholder="z.B. WZ12345"
                              className="h-8 text-xs"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs text-muted-foreground">Zähler-Typ</label>
                            <select
                              value={cfg.zaehler_typ || 'haupt'}
                              onChange={e => setCfgForm(f => ({
                                ...f,
                                location_meter_configs: f.location_meter_configs.map((c, i) =>
                                  i === idx ? { ...c, zaehler_typ: e.target.value } : c
                                )
                              }))}
                              className="h-8 w-full rounded border bg-background px-2 text-xs"
                            >
                              <option value="haupt">Hauptzähler</option>
                              <option value="zwischen">Zwischenzähler</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* Gas / Strom: bestehende Config-UI */
              <div className="space-y-4">
                {isGas ? (
                  <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col rounded-lg border px-4 py-3 gap-2">
                  <div>
                    <div className="text-sm font-medium">Standard-Brennwert (kWh/m³)</div>
                    <div className="text-xs text-muted-foreground">Aus deiner Gasabrechnung</div>
                  </div>
                  <Input type="number" step="0.001" className="w-full text-right"
                    value={cfgForm.meter_config?.default_brennwert || 10.0}
                    onChange={e => setCfgForm(f => ({
                      ...f, meter_config: { ...f.meter_config, default_brennwert: parseFloat(e.target.value) || 10.0 }
                    }))} />
                </div>
                <div className="flex flex-col rounded-lg border px-4 py-3 gap-2">
                  <div>
                    <div className="text-sm font-medium">Standard-Zustandszahl (z-Zahl)</div>
                    <div className="text-xs text-muted-foreground">DVGW-Standard: 0.964</div>
                  </div>
                  <Input type="number" step="0.001" className="w-full text-right"
                    value={cfgForm.meter_config?.default_z_zahl || 0.964}
                    onChange={e => setCfgForm(f => ({
                      ...f, meter_config: { ...f.meter_config, default_z_zahl: parseFloat(e.target.value) || 0.964 }
                    }))} />
                </div>
                </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between rounded-lg border px-4 py-3">
                  <div>
                    <div className="text-sm font-medium">Standard-Wandlerfaktor</div>
                    <div className="text-xs text-muted-foreground">Fallback wenn kein Standort-Faktor hinterlegt</div>
                  </div>
                  <Input type="number" step="0.1" className="w-24 text-right"
                    value={cfgForm.meter_config?.default_factor || 1}
                    onChange={e => setCfgForm(f => ({
                      ...f, meter_config: { ...f.meter_config, default_factor: parseFloat(e.target.value) || 1 }
                    }))} />
                </div>
                <div className="flex items-center justify-between rounded-lg border px-4 py-3">
                  <div className="text-sm font-medium">Foto-Nachweis Pflicht</div>
                  <input type="checkbox" className="h-4 w-4 accent-primary"
                    checked={cfgForm.meter_config?.require_photo !== false}
                    onChange={e => setCfgForm(f => ({
                      ...f, meter_config: { ...f.meter_config, require_photo: e.target.checked }
                    }))} />
                </div>
                  </>
                )}

                {/* Per-Standort-Konfiguration (Strom/Gas) */}
            <div className="space-y-2">
              <label className="text-sm font-semibold">Konfiguration pro Standort</label>
              <p className="text-xs text-muted-foreground">
                {isGas
                  ? 'Brennwert und z-Zahl pro Standort können vom Standard abweichen (steht in der jeweiligen Gasabrechnung).'
                  : 'Bitte die Seriennummer des Zählerkastens eintragen, damit Mitarbeiter den richtigen Zähler identifizieren können.'
                }
              </p>
              <div className="rounded-lg border overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted/40 border-b text-left">
                      <th className="px-3 py-2 font-medium">Standort</th>
                      {isGas ? (
                        <>
                          <th className="px-2 py-2 font-medium">Brennwert (kWh/m³)</th>
                          <th className="px-2 py-2 font-medium">z-Zahl</th>
                        </>
                      ) : (
                        <>
                          <th className="px-2 py-2 font-medium">Zählernummer *</th>
                          <th className="px-2 py-2 font-medium text-center">HT+NT</th>
                          <th className="px-2 py-2 font-medium w-20">Faktor C</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {cfgForm.location_meter_configs.map(cfg => (
                      <tr key={cfg.location_id} className="border-b last:border-0">
                        <td className="px-3 py-1.5 font-medium">{locName(cfg.location_id)}</td>
                        {isGas ? (
                          <>
                            <td className="px-1 py-1">
                              <Input type="number" step="0.001"
                                value={cfg.brennwert || ''}
                                onChange={e => updateLocCfg(cfg.location_id, 'brennwert', parseFloat(e.target.value) || '')}
                                placeholder="10.0" className="h-7 text-xs" />
                            </td>
                            <td className="px-1 py-1">
                              <Input type="number" step="0.001"
                                value={cfg.z_zahl || ''}
                                onChange={e => updateLocCfg(cfg.location_id, 'z_zahl', parseFloat(e.target.value) || '')}
                                placeholder="0.964" className="h-7 text-xs" />
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-1 py-1">
                              <Input value={cfg.zaehler_nr || ''}
                                onChange={e => updateLocCfg(cfg.location_id, 'zaehler_nr', e.target.value)}
                                placeholder="z.B. EV0015823 *" className={`h-7 text-xs ${!cfg.zaehler_nr?.trim() ? 'border-red-300' : ''}`} />
                            </td>
                            <td className="px-1 py-1 text-center">
                              <input type="checkbox" className="h-4 w-4 accent-primary"
                                checked={cfg.has_nt === true}
                                onChange={e => updateLocCfg(cfg.location_id, 'has_nt', e.target.checked)} />
                            </td>
                            <td className="px-1 py-1">
                              <Input type="number" step="0.1" value={cfg.factor || ''}
                                onChange={e => updateLocCfg(cfg.location_id, 'factor', parseFloat(e.target.value) || '')}
                                placeholder="z.B. 50" className="h-7 text-xs" />
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfig(false)}>Abbrechen</Button>
            <Button onClick={handleSaveConfig} disabled={savingCfg} data-testid="save-meter-config-btn">
              {savingCfg ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Speichern…</> : 'Konfiguration speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
